'use client';

import { c as _c } from "react/compiler-runtime";
import type { FundingQuotedAmounts } from '@morfi/onramp-sdk';
import { type FundingOperation, type FundingOperationDetail, formatFiatUnits, fundingNavigationManifest, fundingOperationAmounts, fundingReceivedPresentation, fundingSettlementValue, fundingSourcePresentation, isBankFundingMethod, isFundingMethod, isWithdrawalMethod, type OnrampClient, withdrawalReceipt } from '@morfi/onramp-sdk';
import { useFundingCurrency, useFundingNavigation, useFundingWalletRefresh, useOnrampOperation } from '@morfi/onramp-sdk/react';
import type { OnrampRealtime } from '@morfi/onramp-sdk/realtime';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/app-accordion';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { ArrowLeft01Icon } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { Link } from '@/i18n/navigation';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useExchangeRates } from '@/providers/exchange-rate-provider';
import { useRefreshPortfolioWallet } from '@/queries/wallet-balance';
import { useCurrencyStore } from '@/stores/currency';
import { BankTransferInstructions } from '../bank-transfers/bank-transfer-instructions';
import { FundingExchange } from '../checkout/exchange/funding-exchange';
import { FundingChromeContext, useFundingStage } from '../shared/funding-chrome';
import { FundingError } from '../shared/funding-error';
import { useFundingError } from '../shared/use-funding-error';
import { useFundingPageActive } from '../shared/use-funding-page-active';
import { FundingActionRecovery } from './funding-action-recovery';
import { FundingOperationProgress } from './funding-operation-progress';
import { FundingReceiptActions } from './funding-receipt-actions';
import { useFundingStatusLabels } from './use-funding-status-labels';
interface FundingReceiptProps {
  progress?: boolean;
  initialOperation?: FundingOperation;
  quoted?: FundingQuotedAmounts;
  client: OnrampClient;
  operationId: string;
  realtime?: OnrampRealtime;
  onBack(): void;
  onReturnToPurchase?: (() => void) | undefined;
  onSignIn(): void;
}
export function FundingReceipt(t0) {
  const $ = _c(76);
  const {
    client,
    operationId,
    realtime,
    onBack,
    onReturnToPurchase,
    onSignIn,
    progress: t1,
    initialOperation,
    quoted
  } = t0;
  const progress = t1 === undefined ? false : t1;
  const t = useTranslations("onramp");
  const chrome = useContext(FundingChromeContext);
  const navigation = useFundingNavigation(client);
  const [recoveryId, setRecoveryId] = useState(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const active = useFundingPageActive();
  let t2;
  if ($[0] !== realtime) {
    t2 = realtime ? {
      realtime
    } : {};
    $[0] = realtime;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== active || $[3] !== t2) {
    t3 = {
      active,
      ...t2,
      onRefreshError: _temp
    };
    $[2] = active;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const receipt = useOnrampOperation(client, operationId, t3);
  const announced = useRef(null);
  const operation = receipt.data;
  const purchase = operation?.purchase;
  const originHref = purchase && fundingNavigationManifest.direct_token_purchase.receipt_back === "origin" ? purchase.origin === "winner" ? `/winners/${purchase.mint}` : `/markets/${purchase.marketAddress}?outcome=${encodeURIComponent(purchase.title)}` : undefined;
  let t4;
  if ($[5] !== onBack || $[6] !== onReturnToPurchase || $[7] !== originHref) {
    t4 = function returnToOrigin() {
      if (originHref) {
        (onReturnToPurchase ?? onBack)();
      } else {
        onBack();
      }
    };
    $[5] = onBack;
    $[6] = onReturnToPurchase;
    $[7] = originHref;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const returnToOrigin = t4;
  const t5 = operation?.purchase ? "direct_purchase_title" : "deposit_title";
  let t6;
  if ($[9] !== t || $[10] !== t5) {
    t6 = t(t5);
    $[9] = t;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== instructionsOpen || $[13] !== operationId || $[14] !== recoveryId || $[15] !== returnToOrigin) {
    t7 = instructionsOpen ? () => setInstructionsOpen(false) : recoveryId === operationId ? () => setRecoveryId(null) : returnToOrigin;
    $[12] = instructionsOpen;
    $[13] = operationId;
    $[14] = recoveryId;
    $[15] = returnToOrigin;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  useFundingStage(t6, false, t7, true, undefined, instructionsOpen || recoveryId === operationId ? undefined : originHref);
  let t8;
  let t9;
  if ($[17] !== active || $[18] !== operation || $[19] !== progress || $[20] !== t) {
    t8 = () => {
      if (!progress || !active || !operation) {
        return;
      }
      const key = `${operation.id}:${operation.status}:${operation.walletDelivery?.state}`;
      if (announced.current === key) {
        return;
      }
      announced.current = key;
      showToast({
        id: TOAST_IDS.ONRAMP_PAYMENT_STATUS,
        message: t(operation.status === "delivered" ? operation.walletDelivery?.state === "sandbox_confirmed" ? "progress_sandbox_complete_title" : operation.walletDelivery?.state === "confirmed" ? "status.delivered" : "progress_processing_title" : `status.${operation.status}`),
        variant: operation.status === "failed" ? "error" : operation.status === "delivered" && ["confirmed", "sandbox_confirmed"].includes(operation.walletDelivery?.state ?? "") ? "success" : "info"
      });
    };
    t9 = [progress, active, operation, t];
    $[17] = active;
    $[18] = operation;
    $[19] = progress;
    $[20] = t;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  useFundingError(receipt.error);
  if (instructionsOpen && operation?.method === "sepa") {
    let t10;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = () => setInstructionsOpen(false);
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== client || $[25] !== operationId) {
      t11 = <BankTransferInstructions client={client} operationId={operationId} onDismiss={t10} />;
      $[24] = client;
      $[25] = operationId;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  if (recoveryId === operationId && operation?.method === "cex_connect") {
    let t10;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = () => setRecoveryId(null);
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== client || $[29] !== onSignIn) {
      t11 = id => <FundingReceipt client={client} operationId={id} progress={true} onBack={() => setRecoveryId(null)} onSignIn={onSignIn} />;
      $[28] = client;
      $[29] = onSignIn;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    let t12;
    if ($[31] !== active || $[32] !== client || $[33] !== operationId || $[34] !== t11) {
      t12 = <FundingExchange client={client} open={active} operationId={operationId} onBack={t10} renderStatus={t11} />;
      $[31] = active;
      $[32] = client;
      $[33] = operationId;
      $[34] = t11;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    return t12;
  }
  if (recoveryId === operationId) {
    let t10;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = () => setRecoveryId(null);
      $[36] = t10;
    } else {
      t10 = $[36];
    }
    let t11;
    if ($[37] !== client || $[38] !== operationId) {
      t11 = <FundingActionRecovery key={operationId} client={client} operationId={operationId} onBack={t10} />;
      $[37] = client;
      $[38] = operationId;
      $[39] = t11;
    } else {
      t11 = $[39];
    }
    return t11;
  }
  let t10;
  if ($[40] !== chrome || $[41] !== originHref || $[42] !== returnToOrigin || $[43] !== t) {
    t10 = !chrome && (originHref ? <AnimatedButton variant="tertiary" className="self-start" asChild={true}><Link href={originHref} onClick={returnToOrigin}><Icon icon={ArrowLeft01Icon} />{t("back")}</Link></AnimatedButton> : <AnimatedButton variant="tertiary" prefix={ArrowLeft01Icon} className="self-start" onClick={returnToOrigin}>{t("back")}</AnimatedButton>);
    $[40] = chrome;
    $[41] = originHref;
    $[42] = returnToOrigin;
    $[43] = t;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== initialOperation || $[46] !== navigation || $[47] !== onBack || $[48] !== operationId || $[49] !== progress || $[50] !== quoted || $[51] !== receipt.isPending || $[52] !== t) {
    t11 = receipt.isPending ? progress && initialOperation?.id === operationId ? <FundingOperationProgress operation={initialOperation} {...quoted ? {
      quoted
    } : {}} onReceipt={() => navigation.openReceipt(operationId, "deposit")} onTryAnotherMethod={onBack} /> : <Skeleton className="h-48 w-full" aria-label={t("loading")} /> : null;
    $[45] = initialOperation;
    $[46] = navigation;
    $[47] = onBack;
    $[48] = operationId;
    $[49] = progress;
    $[50] = quoted;
    $[51] = receipt.isPending;
    $[52] = t;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  let t12;
  if ($[54] !== onSignIn || $[55] !== receipt) {
    t12 = receipt.isError ? <FundingError error={receipt.error} onSignIn={onSignIn} onRetry={() => {
      receipt.refetch();
    }} /> : null;
    $[54] = onSignIn;
    $[55] = receipt;
    $[56] = t12;
  } else {
    t12 = $[56];
  }
  let t13;
  if ($[57] !== operationId || $[58] !== receipt.data?.status || $[59] !== receipt.data?.withdrawal || $[60] !== receipt.isError || $[61] !== t) {
    t13 = !receipt.isError && !receipt.data?.withdrawal && receipt.data?.status === "action_required" ? <AnimatedButton onClick={() => setRecoveryId(operationId)}>{t("verify_continue")}</AnimatedButton> : null;
    $[57] = operationId;
    $[58] = receipt.data?.status;
    $[59] = receipt.data?.withdrawal;
    $[60] = receipt.isError;
    $[61] = t;
    $[62] = t13;
  } else {
    t13 = $[62];
  }
  let t14;
  if ($[63] !== active || $[64] !== client || $[65] !== progress || $[66] !== quoted || $[67] !== receipt || $[68] !== t) {
    t14 = !receipt.isError && receipt.data ? <>{receipt.data.withdrawal ? <WithdrawalReceiptContent operation={receipt.data} /> : <ReceiptContent operation={receipt.data} client={client} active={active} progress={progress} {...quoted ? {
        quoted
      } : {}} />}{!receipt.data.withdrawal && isBankFundingMethod(receipt.data.method) && ["awaiting_payment", "processing", "submission_unknown"].includes(receipt.data.status) ? <p className="text-caption text-foreground-secondary">{!receipt.data.withdrawal && receipt.data.method === "sepa" ? t("bank_arrival_estimate") : t("bank_arrival_unconfirmed")}</p> : null}{!receipt.data.withdrawal && receipt.data.method === "sepa" && receipt.data.providerReference && !["delivered", "failed", "cancelled", "refunded"].includes(receipt.data.status) ? <AnimatedButton variant="secondary" onClick={() => setInstructionsOpen(true)}>{t("bank_view_details")}</AnimatedButton> : null}{!progress ? <FundingReceiptActions client={client} onRefresh={async () => {
        await receipt.refetch({
          throwOnError: true
        });
      }} /> : null}</> : null;
    $[63] = active;
    $[64] = client;
    $[65] = progress;
    $[66] = quoted;
    $[67] = receipt;
    $[68] = t;
    $[69] = t14;
  } else {
    t14 = $[69];
  }
  let t15;
  if ($[70] !== t10 || $[71] !== t11 || $[72] !== t12 || $[73] !== t13 || $[74] !== t14) {
    t15 = <div className="rr-block flex flex-col gap-3">{t10}{t11}{t12}{t13}{t14}</div>;
    $[70] = t10;
    $[71] = t11;
    $[72] = t12;
    $[73] = t13;
    $[74] = t14;
    $[75] = t15;
  } else {
    t15 = $[75];
  }
  return t15;
}
function _temp(code) {
  return logger.warn("onramp.receipt_refresh_failed", {
    code
  });
}
function ReceiptContent(t0) {
  const $ = _c(179);
  const {
    operation,
    client,
    active,
    progress,
    quoted
  } = t0;
  const t = useTranslations("onramp");
  const navigation = useFundingNavigation(client);
  const refreshWallet = useRefreshPortfolioWallet();
  const providerDelivered = operation.status === "delivered" && operation.settlementEvidence === "provider_reported";
  const walletConfirmed = providerDelivered && (operation.walletDelivery?.state === "confirmed" || Boolean(operation.purchase && operation.walletDelivery?.state === "sandbox_confirmed")) && (!operation.purchase || operation.walletDelivery?.mint === operation.purchase.mint);
  let t1;
  if ($[0] !== operation.walletDelivery?.state || $[1] !== t) {
    t1 = operation.walletDelivery?.state === "confirmed" ? t("wallet_delivery_confirmed") : operation.walletDelivery?.state === "sandbox_confirmed" ? t("receipt_delivery_sandbox") : t("wallet_delivery_unconfirmed");
    $[0] = operation.walletDelivery?.state;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const deliveryMessage = t1;
  let t2;
  if ($[3] !== operation.purchase) {
    t2 = operation.purchase ? {
      mint: operation.purchase.mint
    } : {};
    $[3] = operation.purchase;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== active || $[6] !== client.cacheKey || $[7] !== operation.destination || $[8] !== operation.id || $[9] !== operation.version || $[10] !== t2 || $[11] !== walletConfirmed) {
    t3 = {
      cacheKey: client.cacheKey,
      operationId: operation.id,
      version: operation.version,
      destination: operation.destination,
      ...t2,
      providerDelivered: walletConfirmed,
      active
    };
    $[5] = active;
    $[6] = client.cacheKey;
    $[7] = operation.destination;
    $[8] = operation.id;
    $[9] = operation.version;
    $[10] = t2;
    $[11] = walletConfirmed;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const walletRefresh = useFundingWalletRefresh(t3, refreshWallet);
  let t4;
  if ($[13] !== t || $[14] !== walletRefresh.state) {
    t4 = walletRefresh.state === "refreshed" ? t("wallet_refreshed") : walletRefresh.state === "unavailable" ? t("wallet_refresh_unavailable") : t("wallet_refreshing");
    $[13] = t;
    $[14] = walletRefresh.state;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const walletMessage = t4;
  const locale = useLocale();
  const statusLabels = useFundingStatusLabels();
  const art = providerDelivered ? "success" : operation.status === "refunded" ? "refund" : operation.status === "failed" || operation.status === "cancelled" ? "declined" : "processing";
  let t5;
  if ($[16] !== locale || $[17] !== operation || $[18] !== t) {
    t5 = units => units === null ? t("amount_pending") : "exchangeSource" in operation || "cryptoDepositSource" in operation || "connectedWalletSource" in operation ? t("exchange_fees_unknown") : formatFiatUnits(units, operation.fiatDecimals, operation.fiatCurrency, locale);
    $[16] = locale;
    $[17] = operation;
    $[18] = t;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const format = t5;
  let t6;
  if ($[20] !== operation.status || $[21] !== providerDelivered) {
    t6 = ["created", "action_required", "awaiting_payment", "submission_unknown", "processing"].includes(operation.status) || operation.status === "delivered" && !providerDelivered;
    $[20] = operation.status;
    $[21] = providerDelivered;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const pending = t6;
  let received;
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  if ($[23] !== format || $[24] !== locale || $[25] !== operation || $[26] !== quoted || $[27] !== t) {
    const amounts = fundingOperationAmounts(operation, quoted);
    let t12;
    if ($[34] !== locale || $[35] !== operation || $[36] !== quoted) {
      t12 = fundingReceivedPresentation(operation, locale, quoted);
      $[34] = locale;
      $[35] = operation;
      $[36] = quoted;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    received = t12;
    let t13;
    if ($[38] !== operation || $[39] !== t) {
      t13 = "exchangeSource" in operation ? t("exchange_coinbase") : "cryptoDepositSource" in operation ? t("crypto_deposit_title") : "connectedWalletSource" in operation ? t("connected_wallet_title") : isFundingMethod(operation.method) ? t(operation.method) : t("hosted_payment_method");
      $[38] = operation;
      $[39] = t;
      $[40] = t13;
    } else {
      t13 = $[40];
    }
    const paymentMethodLabel = t13;
    let t14;
    if ($[41] !== t) {
      t14 = t("hosted_payment_method");
      $[41] = t;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    if ($[43] !== paymentMethodLabel || $[44] !== t14) {
      t9 = [t14, paymentMethodLabel];
      $[43] = paymentMethodLabel;
      $[44] = t14;
      $[45] = t9;
    } else {
      t9 = $[45];
    }
    let t15;
    if ($[46] !== t) {
      t15 = t("principal");
      $[46] = t;
      $[47] = t15;
    } else {
      t15 = $[47];
    }
    let t16;
    if ($[48] !== locale || $[49] !== operation || $[50] !== t) {
      t16 = fundingSourcePresentation(operation, locale) ?? t("amount_pending");
      $[48] = locale;
      $[49] = operation;
      $[50] = t;
      $[51] = t16;
    } else {
      t16 = $[51];
    }
    if ($[52] !== t15 || $[53] !== t16) {
      t10 = [t15, t16];
      $[52] = t15;
      $[53] = t16;
      $[54] = t10;
    } else {
      t10 = $[54];
    }
    t11 = [t("fees"), format(amounts.feeUnits)];
    if ($[55] !== t) {
      t7 = t("total_charged");
      $[55] = t;
      $[56] = t7;
    } else {
      t7 = $[56];
    }
    t8 = format(amounts.chargedUnits);
    $[23] = format;
    $[24] = locale;
    $[25] = operation;
    $[26] = quoted;
    $[27] = t;
    $[28] = received;
    $[29] = t10;
    $[30] = t11;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    received = $[28];
    t10 = $[29];
    t11 = $[30];
    t7 = $[31];
    t8 = $[32];
    t9 = $[33];
  }
  let t12;
  if ($[57] !== t7 || $[58] !== t8) {
    t12 = [t7, t8];
    $[57] = t7;
    $[58] = t8;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== operation.purchase || $[61] !== operation.walletDelivery?.receivedUnits || $[62] !== received || $[63] !== t || $[64] !== walletConfirmed) {
    t13 = !walletConfirmed || !operation.walletDelivery?.receivedUnits ? [] : [[operation.purchase ? t("direct_purchase_quantity") : t("cash_received_estimated"), received.value ?? t("amount_pending")]];
    $[60] = operation.purchase;
    $[61] = operation.walletDelivery?.receivedUnits;
    $[62] = received;
    $[63] = t;
    $[64] = walletConfirmed;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  let t14;
  if ($[66] !== t) {
    t14 = t("reference");
    $[66] = t;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  let t15;
  if ($[68] !== operation.id || $[69] !== t14) {
    t15 = [t14, operation.id];
    $[68] = operation.id;
    $[69] = t14;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  let t16;
  if ($[71] !== operation.providerReference || $[72] !== t) {
    t16 = operation.providerReference ? [[t("provider_reference"), operation.providerReference]] : [];
    $[71] = operation.providerReference;
    $[72] = t;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  let t17;
  if ($[74] !== operation.providerStatus || $[75] !== t) {
    t17 = operation.providerStatus ? [[t("provider_status"), operation.providerStatus]] : [];
    $[74] = operation.providerStatus;
    $[75] = t;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[77] !== t) {
    t18 = t("receipt_environment");
    $[77] = t;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== operation.environment || $[80] !== t) {
    t19 = operation.environment === "sandbox" ? t("environment_sandbox") : t("environment_production");
    $[79] = operation.environment;
    $[80] = t;
    $[81] = t19;
  } else {
    t19 = $[81];
  }
  let t20;
  if ($[82] !== t18 || $[83] !== t19) {
    t20 = [t18, t19];
    $[82] = t18;
    $[83] = t19;
    $[84] = t20;
  } else {
    t20 = $[84];
  }
  let t21;
  if ($[85] !== t) {
    t21 = t("receipt_network");
    $[85] = t;
    $[86] = t21;
  } else {
    t21 = $[86];
  }
  let t22;
  if ($[87] !== operation.network || $[88] !== t) {
    t22 = operation.network === "devnet" ? t("network_devnet") : t("network_mainnet");
    $[87] = operation.network;
    $[88] = t;
    $[89] = t22;
  } else {
    t22 = $[89];
  }
  let t23;
  if ($[90] !== t21 || $[91] !== t22) {
    t23 = [t21, t22];
    $[90] = t21;
    $[91] = t22;
    $[92] = t23;
  } else {
    t23 = $[92];
  }
  let t24;
  if ($[93] !== t) {
    t24 = t("destination");
    $[93] = t;
    $[94] = t24;
  } else {
    t24 = $[94];
  }
  let t25;
  if ($[95] !== operation.destination || $[96] !== t24) {
    t25 = [t24, operation.destination];
    $[95] = operation.destination;
    $[96] = t24;
    $[97] = t25;
  } else {
    t25 = $[97];
  }
  let t26;
  if ($[98] !== deliveryMessage || $[99] !== providerDelivered || $[100] !== t) {
    t26 = providerDelivered ? [[t("receipt_delivery"), deliveryMessage]] : [];
    $[98] = deliveryMessage;
    $[99] = providerDelivered;
    $[100] = t;
    $[101] = t26;
  } else {
    t26 = $[101];
  }
  let t27;
  if ($[102] !== t10 || $[103] !== t11 || $[104] !== t12 || $[105] !== t13 || $[106] !== t15 || $[107] !== t16 || $[108] !== t17 || $[109] !== t20 || $[110] !== t23 || $[111] !== t25 || $[112] !== t26 || $[113] !== t9) {
    t27 = [t9, t10, t11, t12, ...t13, t15, ...t16, ...t17, t20, t23, t25, ...t26];
    $[102] = t10;
    $[103] = t11;
    $[104] = t12;
    $[105] = t13;
    $[106] = t15;
    $[107] = t16;
    $[108] = t17;
    $[109] = t20;
    $[110] = t23;
    $[111] = t25;
    $[112] = t26;
    $[113] = t9;
    $[114] = t27;
  } else {
    t27 = $[114];
  }
  const rows = t27;
  if (progress) {
    let t28;
    if ($[115] !== quoted) {
      t28 = quoted ? {
        quoted
      } : {};
      $[115] = quoted;
      $[116] = t28;
    } else {
      t28 = $[116];
    }
    let t29;
    if ($[117] !== navigation || $[118] !== operation.id) {
      t29 = () => navigation.openReceipt(operation.id, "deposit");
      $[117] = navigation;
      $[118] = operation.id;
      $[119] = t29;
    } else {
      t29 = $[119];
    }
    let t30;
    if ($[120] !== navigation.deposit || $[121] !== operation || $[122] !== t28 || $[123] !== t29) {
      t30 = <FundingOperationProgress operation={operation} {...t28} onReceipt={t29} onTryAnotherMethod={navigation.deposit} />;
      $[120] = navigation.deposit;
      $[121] = operation;
      $[122] = t28;
      $[123] = t29;
      $[124] = t30;
    } else {
      t30 = $[124];
    }
    return t30;
  }
  const t28 = operation.purchase?.imageNsfw ?? false;
  const t29 = operation.purchase?.imageUrl || `/assets/onramp/status-${art}.png`;
  let t30;
  if ($[125] !== t28 || $[126] !== t29) {
    t30 = <OptimizedImage nsfw={t28} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src={t29} alt="" size="size-24" className="bg-transparent" />;
    $[125] = t28;
    $[126] = t29;
    $[127] = t30;
  } else {
    t30 = $[127];
  }
  let t31;
  if ($[128] !== operation.purchase || $[129] !== operation.purchaseFulfillment || $[130] !== operation.status || $[131] !== providerDelivered || $[132] !== statusLabels || $[133] !== t || $[134] !== walletConfirmed) {
    t31 = operation.purchase ? operation.purchaseFulfillment?.state === "cash_added" && operation.purchaseFulfillment.cashReceivedD6 ? t("direct_purchase_cash_added") : walletConfirmed ? t("direct_purchase_received") : operation.status === "failed" || operation.status === "refunded" || operation.status === "cancelled" ? statusLabels[operation.status] : t("direct_purchase_pending") : providerDelivered ? t("provider_completed") : statusLabels[operation.status === "delivered" ? "processing" : operation.status];
    $[128] = operation.purchase;
    $[129] = operation.purchaseFulfillment;
    $[130] = operation.status;
    $[131] = providerDelivered;
    $[132] = statusLabels;
    $[133] = t;
    $[134] = walletConfirmed;
    $[135] = t31;
  } else {
    t31 = $[135];
  }
  let t32;
  if ($[136] !== t31) {
    t32 = <h3 className="text-heading-md">{t31}</h3>;
    $[136] = t31;
    $[137] = t32;
  } else {
    t32 = $[137];
  }
  let t33;
  if ($[138] !== locale || $[139] !== operation || $[140] !== received || $[141] !== t || $[142] !== walletConfirmed) {
    t33 = operation.purchase && walletConfirmed ? received.value : fundingSourcePresentation(operation, locale) ?? t("amount_pending");
    $[138] = locale;
    $[139] = operation;
    $[140] = received;
    $[141] = t;
    $[142] = walletConfirmed;
    $[143] = t33;
  } else {
    t33 = $[143];
  }
  let t34;
  if ($[144] !== t33) {
    t34 = <p className="text-display font-semibold">{t33}</p>;
    $[144] = t33;
    $[145] = t34;
  } else {
    t34 = $[145];
  }
  let t35;
  if ($[146] !== operation.purchase) {
    t35 = operation.purchase && <p className="text-body">{operation.purchase.title}</p>;
    $[146] = operation.purchase;
    $[147] = t35;
  } else {
    t35 = $[147];
  }
  let t36;
  if ($[148] !== t || $[149] !== walletConfirmed || $[150] !== walletMessage || $[151] !== walletRefresh.retry || $[152] !== walletRefresh.state) {
    t36 = walletConfirmed && walletRefresh.state === "unavailable" ? <div className="flex flex-col items-center gap-2 text-caption text-foreground-secondary" aria-live="polite">{walletConfirmed ? <p>{walletMessage}</p> : null}{walletConfirmed && walletRefresh.state === "unavailable" ? <AnimatedButton variant="secondary" onClick={walletRefresh.retry}>{t("retry")}</AnimatedButton> : null}</div> : null;
    $[148] = t;
    $[149] = walletConfirmed;
    $[150] = walletMessage;
    $[151] = walletRefresh.retry;
    $[152] = walletRefresh.state;
    $[153] = t36;
  } else {
    t36 = $[153];
  }
  let t37;
  if ($[154] !== pending || $[155] !== t) {
    t37 = pending ? <p className="text-caption text-foreground-secondary">{t("receipt_pending")}</p> : null;
    $[154] = pending;
    $[155] = t;
    $[156] = t37;
  } else {
    t37 = $[156];
  }
  let t38;
  if ($[157] !== t30 || $[158] !== t32 || $[159] !== t34 || $[160] !== t35 || $[161] !== t36 || $[162] !== t37) {
    t38 = <div className="flex flex-col items-center gap-2 pt-2 pb-3 text-center">{t30}{t32}{t34}{t35}{t36}{t37}</div>;
    $[157] = t30;
    $[158] = t32;
    $[159] = t34;
    $[160] = t35;
    $[161] = t36;
    $[162] = t37;
    $[163] = t38;
  } else {
    t38 = $[163];
  }
  let t39;
  if ($[164] !== rows) {
    t39 = <dl className="flex flex-col gap-3">{rows.slice(0, 4).map(_temp2)}</dl>;
    $[164] = rows;
    $[165] = t39;
  } else {
    t39 = $[165];
  }
  let t40;
  if ($[166] !== t) {
    t40 = t("receipt_more_information");
    $[166] = t;
    $[167] = t40;
  } else {
    t40 = $[167];
  }
  let t41;
  if ($[168] !== t40) {
    t41 = <AccordionTrigger className="min-h-11 items-center py-3 text-caption text-foreground-secondary hover:no-underline">{t40}</AccordionTrigger>;
    $[168] = t40;
    $[169] = t41;
  } else {
    t41 = $[169];
  }
  let t42;
  if ($[170] !== rows) {
    t42 = <AccordionContent className="pt-3 pb-0"><dl className="flex flex-col gap-3">{rows.slice(4).map(_temp3)}</dl></AccordionContent>;
    $[170] = rows;
    $[171] = t42;
  } else {
    t42 = $[171];
  }
  let t43;
  if ($[172] !== t41 || $[173] !== t42) {
    t43 = <Accordion type="single" collapsible={true}><AccordionItem value="details" className="border-0">{t41}{t42}</AccordionItem></Accordion>;
    $[172] = t41;
    $[173] = t42;
    $[174] = t43;
  } else {
    t43 = $[174];
  }
  let t44;
  if ($[175] !== t38 || $[176] !== t39 || $[177] !== t43) {
    t44 = <div className="flex flex-col gap-3">{t38}{t39}{t43}</div>;
    $[175] = t38;
    $[176] = t39;
    $[177] = t43;
    $[178] = t44;
  } else {
    t44 = $[178];
  }
  return t44;
}
function _temp3(t0) {
  const [label_0, value_0] = t0;
  return <div key={label_0} className="flex items-start justify-between gap-4 text-body"><dt className="shrink-0 text-foreground-secondary">{label_0}</dt><dd className="select-all break-all text-end">{value_0}</dd></div>;
}
function _temp2(t0) {
  const [label, value] = t0;
  return <div key={label} className="flex items-start justify-between gap-4 text-body"><dt className="shrink-0 text-foreground-secondary">{label}</dt><dd className="select-all break-all text-end">{value}</dd></div>;
}
export function WithdrawalReceiptContent(t0) {
  const $ = _c(2);
  const {
    operation
  } = t0;
  const rates = useExchangeRates();
  const selectedCurrency = useCurrencyStore(_temp4);
  const currency = useFundingCurrency(selectedCurrency, rates.fundingFx);
  const withdrawalMoney = function withdrawalMoney(amountD6) {
    return currency.status === "ready" && currency.fx ? formatFiatUnits(fundingSettlementValue(amountD6, currency.currency, currency.decimals, currency.fx), currency.decimals, currency.currency, locale) : t("amount_pending");
  };
  const t = useTranslations("onramp");
  const locale = useLocale();
  if (!operation.withdrawal || !("fiatCurrency" in operation)) {
    throw new Error("offramp_evidence_missing");
  }
  const receipt = withdrawalReceipt(operation);
  const rows = [[t("withdraw_source"), receipt.source], [t("withdraw_network"), operation.network === "devnet" ? "Solana devnet" : "Solana"], [t("payout_method"), t(isWithdrawalMethod(operation.method) ? operation.method : "hosted_payment_method")], [t("withdraw_fees"), operation.withdrawal?.kind === "manual" ? formatFiatUnits("0", operation.fiatDecimals, operation.fiatCurrency, locale) : formatFiatUnits(operation.feeUnits ?? "0", operation.fiatDecimals, operation.fiatCurrency, locale)], [t("reference"), operation.id], ...(receipt.recipient ? [[t("recipient_wallet"), receipt.recipient]] : []), ...(receipt.providerId ? [[t("provider_reference"), receipt.providerId]] : []), ...(receipt.payoutUnits ? [[t("estimated_payout"), formatFiatUnits(receipt.payoutUnits, operation.fiatDecimals, operation.fiatCurrency, locale)]] : [])];
  const t1 = `/assets/onramp/status-${receipt.artwork}.png`;
  let t2;
  if ($[0] !== t1) {
    t2 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src={t1} className="bg-transparent" alt="" size="size-24" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return <div className="flex flex-col gap-4"><div className="flex flex-col items-center gap-2 text-center">{t2}<h3 className="text-heading-md">{t(receipt.title)}</h3><p className="text-display font-semibold">{withdrawalMoney(operation.withdrawal?.amountD6 ?? "0")}</p>{operation.status === "submission_unknown" || operation.withdrawal?.cancellationRequested && operation.status === "action_required" ? <p>{t("withdraw_unknown_hint")}</p> : null}{receipt.title === "withdraw_requote" ? <p>{t("withdraw_requote_hint")}</p> : null}{receipt.complete && operation.withdrawal?.kind === "sell" ? <p>{t("withdraw_payout_hint")}</p> : null}</div><dl className="flex flex-col gap-3">{rows.map(_temp5)}</dl>{receipt.explorer ? <a href={receipt.explorer} target="_blank" rel="noopener noreferrer" className="text-center text-caption">{t("view_explorer")}</a> : null}</div>;
}
function _temp5(t0) {
  const [label, value] = t0;
  return <div key={label} className="flex justify-between gap-4 text-caption"><dt>{label}</dt><dd className="select-all break-all text-end">{value}</dd></div>;
}
function _temp4(state) {
  return state.currency;
}